import { Injectable, inject } from '@angular/core';
import { PACKAGE_CONTENT_KEYS } from '@app/constant/package-content-keys';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import type { PackageDetails, PackageLookupContext } from '@app/interface/package/package-details.interface';
import type { TranslateProviderService } from '@app/interface/service/translate-provider-service.interface';
import type { ProductCatalogStore } from '@app/interface/store/product-catalog-store.interface';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { getItemDisplayName } from '@app/service/item-display.rules';
import {
  getAllPackageItems,
  getDigitalDeliveredCount,
  getPackageProducts,
  getPackageSheets,
  getPrintsDeliveredCount,
  resolveTag,
  toDeliveryCount
} from './package-details.rules';
import type { Package } from '@app/interface/store/package.interface';
import Pilot from '@app/constant/Pilot';
import type { IJobStore } from '@app/interface/job/job-store.interface';

@Injectable({ providedIn: 'root' })
export class PackageDetailsResolver {
  private readonly contentStore = inject<IContentStore>(angularjs.ContentStore);
  private readonly translate = inject<TranslateProviderService>(angularjs.TranslateProviderService);
  private readonly jobStore = inject<IJobStore>(angularjs.JobStore);
  private readonly catalog = inject<ProductCatalogStore>(angularjs.ProductCatalogStore);
  private readonly contentChange = inject(angularjs.ContentChangeAction);
  private readonly activeJobChange = inject(angularjs.ActiveJobChangedAction);
  private readonly cache = new Map<string, PackageDetails>();

  constructor() {
    // Content reloads on locale change and dispatches ContentChangeAction
    // (ContentStore.js:162-164). Clearing here guarantees we never answer from
    // stale content; repainting the AngularJS tile is the caller's job.
    this.contentChange.register(() => this.cache.clear());
    this.activeJobChange.register(() => this.cache.clear());
  }

  resolve({ sku }: PackageLookupContext): PackageDetails | undefined {
    const cached = this.cache.get(sku);
    if (cached) return cached;

    const pkg = this.catalog.findPackageBySku(sku);
    if (!pkg) return undefined;

    const details = this.build(pkg);
    this.cache.set(sku, details);
    return details;
  }

  /** Drops one SKU from the cache; use after anything that changes a package. */
  invalidate(sku: string): void {
    this.cache.delete(sku);
  }

  private build(pkg: Package): PackageDetails {
    const zone: string = pkg.standardizedPricingContentZone || '';
    const read = (z: string, code: string) => this.contentStore.getContent(z || null, code);

    const displayNameOverride = zone ? read(zone, PACKAGE_CONTENT_KEYS.DISPLAY_NAME) : undefined;
    const description = zone ? read(zone, PACKAGE_CONTENT_KEYS.DESCRIPTION) : undefined;

    const printsOverride = zone ? toDeliveryCount(read(zone, PACKAGE_CONTENT_KEYS.PRINTS_COUNT_OVERRIDE)) : undefined;
    const digitalOverride = zone ? toDeliveryCount(read(zone, PACKAGE_CONTENT_KEYS.DIGITAL_COUNT_OVERRIDE)) : undefined;

    const tag = zone
      ? resolveTag(read, { zone, label: pkg.labels?.[0], isPbtfPilot: this.isPbtfPilot(pkg) })
      : { label: undefined, tagStyle: undefined };

    return {
      skuCode: pkg.skuCode,
      zone,
      displayName: displayNameOverride ?? getItemDisplayName(pkg, this.translate.getLocale()),
      hasDescription: description != null && description !== '',
      printsDeliveredCount: printsOverride ?? getPrintsDeliveredCount(pkg.items),
      digitalDeliveredCount: digitalOverride ?? getDigitalDeliveredCount(pkg.items),
      // Catalog truth. Never content-driven — decision 2, defects #1 and #5.
      digitalRightsEligible: pkg.digitalRightsEligible,
      label: tag.label,
      tagStyle: tag.tagStyle,
      allItems: getAllPackageItems(pkg),
      sheets: getPackageSheets(pkg),
      products: getPackageProducts(pkg)
    };
  }

  private isPbtfPilot(pkg: Package): boolean {
    const isPbtfPilotJob = this.jobStore.isInPilot(Pilot.PBTF);
    const names: string[] = (window as any).pbtfPackageNames?.split(',') ?? [];
    return isPbtfPilotJob && names.includes(pkg.nameDesc);
  }
}
